/**
 * Exif Orientation (1〜8) と、それを打ち消すために必要な変換の対応表。
 *
 * Orientation は「格納されている画素をどう変換すれば正しい向きで表示できるか」を表す。
 * 本ユーティリティはその変換を画素に焼き込み、Orientation を 1 に落とすために使う。
 */

import { JpegFrameInfo } from './jpeg-segment';

/** 変換なし。この値なら画像に一切手を加えない */
export const ORIENTATION_TOP_LEFT = 1;

/** canvas の setTransform(a, b, c, d, e, f) に渡す係数 */
export type CanvasMatrix = [number, number, number, number, number, number];

type OrientationSpec = {
  /** jpegtran に渡す変換引数 */
  jpegtranArgs: string[],
  /** 縦横が入れ替わるか */
  swapsAxis: boolean,
  /**
   * ロスレス変換が可能かどうか。libjpeg の jtransform_perfect_transform() と同じ判定。
   * MCU 境界に満たない端のブロックは変換できないため、辺が MCU サイズの倍数である必要がある。
   */
  isLossless: (frame: JpegFrameInfo) => boolean,
  /** 幅 w / 高さ h の画像を正しい向きで描くための変換行列 */
  canvasMatrix: (w: number, h: number) => CanvasMatrix,
};

const losslessAlways = () => true;
const losslessByWidth = (frame: JpegFrameInfo) => frame.width % frame.mcuWidth === 0;
const losslessByHeight = (frame: JpegFrameInfo) => frame.height % frame.mcuHeight === 0;
const losslessByBoth = (frame: JpegFrameInfo) => losslessByWidth(frame) && losslessByHeight(frame);

const SPECS: Record<number, OrientationSpec> = {
  /* 2: 左右反転 */
  2: {
    jpegtranArgs: ['-flip', 'horizontal'],
    swapsAxis: false,
    isLossless: losslessByWidth,
    canvasMatrix: (w) => [-1, 0, 0, 1, w, 0],
  },
  /* 3: 180度回転 */
  3: {
    jpegtranArgs: ['-rotate', '180'],
    swapsAxis: false,
    isLossless: losslessByBoth,
    canvasMatrix: (w, h) => [-1, 0, 0, -1, w, h],
  },
  /* 4: 上下反転 */
  4: {
    jpegtranArgs: ['-flip', 'vertical'],
    swapsAxis: false,
    isLossless: losslessByHeight,
    canvasMatrix: (w, h) => [1, 0, 0, -1, 0, h],
  },
  /* 5: 主対角線で反転 */
  5: {
    jpegtranArgs: ['-transpose'],
    swapsAxis: true,
    isLossless: losslessAlways,
    canvasMatrix: () => [0, 1, 1, 0, 0, 0],
  },
  /* 6: 時計回りに90度回転 */
  6: {
    jpegtranArgs: ['-rotate', '90'],
    swapsAxis: true,
    isLossless: losslessByHeight,
    canvasMatrix: (w, h) => [0, 1, -1, 0, h, 0],
  },
  /* 7: 副対角線で反転 */
  7: {
    jpegtranArgs: ['-transverse'],
    swapsAxis: true,
    isLossless: losslessByBoth,
    canvasMatrix: (w, h) => [0, -1, -1, 0, h, w],
  },
  /* 8: 反時計回りに90度回転 */
  8: {
    jpegtranArgs: ['-rotate', '270'],
    swapsAxis: true,
    isLossless: losslessByWidth,
    canvasMatrix: (w) => [0, -1, 1, 0, 0, w],
  },
};

/**
 * 変換が必要な Orientation か。1 と未知の値は「不要」として扱う。
 */
export const needsTransform = (orientation: number): boolean => Boolean(SPECS[orientation]);

/**
 * Orientation に対応する変換仕様を返す。変換不要なら null。
 */
export const getOrientationSpec = (orientation: number): OrientationSpec | null => SPECS[orientation] ?? null;
